import type { SystemStyleObject } from "@chakra-ui/react";
import { lrnCorrectIcon, lrnIncorrectIcon } from "./common";

// Shared styles extracted for reusability
const hideDefaultFeedback = {
  "border": "0 !important",
  "backgroundImage": "none !important",
  "boxShadow": "none !important",

  "&:after": {
    display: "none"
  }
};

const feedbackIconBase = {
  content: "\"\"",
  width: "24px",
  height: "24px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "absolute",
  left: "8px",
  top: "50%",
  transform: "translateY(-50%)",
  fontSize: "sm",
  fontWeight: "bold",
  color: "base.200",
  pointerEvents: "none",

  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  backgroundSize: "24px 24px"
};

const dragDropFeedbackBase = {
  "background": "base.200 !important",
  "backgroundColor": "base.200 !important",
  "borderWidth": "2px",
  "borderStyle": "solid",
  "borderRadius": "5px",

  "&:after": {
    display: "none"
  },

  "&:before": feedbackIconBase
};

export const lrnAssociationStylings: SystemStyleObject = {
  "& .lrn_qr.lrn_association": {
    "& .lrn_icon": {
      display: "inline-flex !important",
      alignItems: "center",
      justifyContent: "center",
      width: "16px",
      height: "16px",
      opacity: "1 !important",
      color: "base.700 !important",
      visibility: "visible !important"
    },

    "& .lrn_icon:before": {
      position: "static !important",
      top: "auto !important",
      paddingRight: "0 !important",
      lineHeight: "1 !important",
      display: "inline-block !important",
      verticalAlign: "middle"
    },

    "& .lrn_possibilityList.lrn_dragdrop .lrn_btn_drag, & .lrn_response_container.lrn_dragdrop .lrn_btn_drag":
      {
        background: "base.200",
        color: "base.700"
      },

    "& .lrn_btn_drag.lrn_correct, & .lrn_btn_drag.lrn_incorrect": {
      background: "base.200",
      color: "base.700"
    },

    "& .lrn_response": {
      "& .lrn_assoc_table": {
        padding: 0
      },

      "& .lrn_assoc_row .lrn_arrow": {
        "position": "relative",
        "display": "inline-block",
        "height": "1px",
        "background": "lines",
        "borderBottom": "1px",
        "borderBottomStyle": "solid",
        "borderBottomColor": "footerDivider",
        "left": "5px",
        "width": "75%",

        "&::before": {
          content: "''",
          position: "absolute",
          left: "-8px",
          top: "50%",
          transform: "translateY(-50%)",
          width: 0,
          height: 0,
          borderRadius: "0",
          border: "unset",
          borderTop: "6px solid transparent",
          borderBottom: "6px solid transparent",
          borderRight: "8px",
          borderRightStyle: "solid",
          borderRightColor: "footerDivider"
        },

        "&::after": {
          content: "''",
          position: "absolute",
          right: "-8px",
          top: "50%",
          transform: "translateY(-50%)",
          width: 0,
          height: 0,
          borderRadius: "0",
          border: "unset",
          borderTop: "6px solid transparent",
          borderBottom: "6px solid transparent",
          borderLeft: "8px",
          borderLeftStyle: "solid",
          borderLeftColor: "footerDivider"
        }
      },

      "& .lrn_assoc_question": {
        backgroundColor: "gray.200",
        border: 2,
        borderStyle: "solid",
        borderColor: "lines",
        borderRadius: "md",
        color: "base.700"
      },

      "& .lrn_dropzone.lrn_response_container": {
        borderColor: "lines",
        borderRadius: "md"
      }
    },

    "& .lrn_possibilityList.lrn_list_bottom": {
      "&:before": {
        display: "none"
      }
    },

    "& .lrn_display_correct_answer_enabled .lrn_possibilityList": {
      display: "none !important"
    },

    "& .lrn_response_container.lrn_dropzone.lrn_correct_marker, & .lrn_response_container.lrn_dropzone.lrn_incorrect_marker":
      {
        border: "none !important",
        borderColor: "transparent !important",
        boxShadow: "none !important"
      },

    "& .lrn_response_input_wrapper.lrn_incorrect, & .lrn_response_input_wrapper.lrn_correct":
      hideDefaultFeedback,

    "& .lrn_response_container.lrn_dragdrop .lrn_incorrect, & .lrn_response_container.lrn_dragdrop .lrn_correct":
      dragDropFeedbackBase,

    "& .lrn_response_container.lrn_dragdrop": {
      "& .lrn_btn_drag.lrn_correct, & .lrn_btn_drag.lrn_incorrect": {
        display: "flex",
        justifyContent: "flex-start !important",
        alignItems: "center",
        textAlign: "left !important",
        background: "base.200",
        width: "100%"
      },

      // Make the text span fill the row so left-align is visible
      "& .lrn_btn_drag.lrn_correct .lrn_item, & .lrn_btn_drag.lrn_incorrect .lrn_item":
        {
          display: "block",
          width: "100%",
          textAlign: "left !important"
        },

      "& .lrn_btn_drag": {
        "& .lrn_responseIndex": {
          display: "none"
        }
      },

      "& .lrn_qr.lrn_association .lrn_possibilityList": {
        display: "none !important"
      },

      "& .lrn_incorrect": {
        "borderColor": "danger.500",

        "&:before": {
          ...feedbackIconBase,
          ...lrnIncorrectIcon
        },

        "& .lrn_item": {
          paddingLeft: "0.5rem !important"
        }
      },

      "& .lrn_correct": {
        "borderColor": "success.700",

        "&:before": {
          ...feedbackIconBase,
          ...lrnCorrectIcon
        },

        "& .lrn_item": {
          paddingLeft: "0.5rem !important"
        }
      }
    },

    "& .lrn_display_correct_answer_enabled .lrn_response_container.lrn_dragdrop":
      {
        "& .lrn_incorrect, .lrn_correct": {
          borderLeft: "2px",
          borderStyle: "solid"
        },

        "& .lrn_incorrect": {
          borderColor: "danger.500"
        },

        "& .lrn_correct": {
          borderColor: "success.700"
        }
      },

    "& .lrn_response_container.lrn_dragdrop .lrn_btn_drag:not(.lrn_correct):not(.lrn_incorrect)":
      {
        "borderColor": "transparent !important",
        "borderWidth": "0 !important",
        "backgroundColor": "base.200 !important",
        "color": "base.700 !important",

        "&:focus, &:focus-visible, &:active, &[aria-pressed='true'], &.lrn_active":
          {
            borderColor: "lines !important",
            borderWidth: "1px !important",
            boxShadow: "none !important",
            outline: "none !important",
            backgroundColor: "base.200 !important"
          }
      },

    "& .lrn_response_container.lrn_dropzone .lrn_btn_drag:not(.lrn_correct):not(.lrn_incorrect) .lrn_icon":
      {
        display: "none !important",
        width: "0 !important",
        minWidth: "0 !important",
        margin: "0 !important",
        padding: "0 !important"
      },

    "& .lrn_response_container.lrn_dropzone .lrn_btn_drag:not(.lrn_correct):not(.lrn_incorrect) .lrn_item":
      {
        paddingLeft: "0 !important",
        marginLeft: "0 !important",
        width: "100% !important"
      },

    "& .lrn_response_container.lrn_dropzone .lrn_btn_drag.lrn_correct .lrn_item, & .lrn_response_container.lrn_dropzone .lrn_btn_drag.lrn_incorrect .lrn_item":
      {
        paddingLeft: "0.5rem !important"
      },

    "& .lrn_response_container.lrn_dropzone .lrn_btn_drag": {
      position: "relative"
    }
  },

  "& div[data-correct-answer-clone] .lrn_qr.lrn_association": {
    "& .lrn_response .lrn_dropzone.lrn_response_container": {
      "border": 2,
      "borderStyle": "solid",
      "borderColor": "success.700",
      "borderRadius": "md",

      "& .lrn_responseText": {
        "color": "var(--euka-colors-base-700) !important",
        "padding": "10px",
        "paddingLeft": "68px",

        "&:before": {
          ...feedbackIconBase,
          ...lrnCorrectIcon
        }
      }
    },

    "& .lrn_display_correct_answer_enabled .lrn_response_container.lrn_dragdrop .lrn_incorrect": {
      "&:before": {
        display: "none"
      }
    }
  }
};
